import { type CSSProperties, type FormEvent, useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useSubmitFeedback } from '../api/hooks.js';
import { getRecentDiagnostics } from '../diagnostics.js';
import { useAppStore } from './store.js';

type Area = 'general' | 'data' | 'workflow' | 'performance' | 'bug' | 'feature';
type Category = 'bug' | 'performance' | 'feature_request' | 'data_issue' | 'ux_confusion';
type Severity = 'blocker' | 'painful' | 'minor';

const CATEGORIES: Array<{ value: Category; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'performance', label: 'Performance' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'data_issue', label: 'Data issue' },
  { value: 'ux_confusion', label: 'UX confusion' },
];

const SEVERITIES: Array<{ value: Severity; label: string }> = [
  { value: 'painful', label: 'Painful' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'minor', label: 'Minor' },
];

export function FeedbackModal({
  open,
  onOpenChange,
  userDisplayName,
  userEmail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userDisplayName: string;
  userEmail?: string;
}) {
  const submitFeedback = useSubmitFeedback();
  const [category, setCategory] = useState<Category>('bug');
  const [severity, setSeverity] = useState<Severity>('painful');
  const [description, setDescription] = useState('');
  const [expected, setExpected] = useState('');
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const diagnosticCount = useMemo(() => getRecentDiagnostics().length, [open]);
  const canSubmit = description.trim().length >= 8 && !submitFeedback.isPending;

  // Depend on `reset`, NOT the whole mutation object: useMutation returns a
  // freshly-allocated result every render, so `[open, submitFeedback]` re-ran
  // this effect on each pass — reset() re-rendered, which minted a new object,
  // which re-ran the effect → "Maximum update depth exceeded". `reset` is a
  // stable reference (React Query guarantees it), so the effect now fires only
  // when the modal actually opens/closes.
  const { reset: resetMutation } = submitFeedback;
  useEffect(() => {
    if (!open) {
      resetMutation();
      setSubmittedId(null);
    }
  }, [open, resetMutation]);

  function resetForm() {
    setCategory('bug');
    setSeverity('painful');
    setDescription('');
    setExpected('');
    setIncludeDiagnostics(true);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    const res = await submitFeedback.mutateAsync({
      area: areaForCategory(category),
      category,
      severity,
      description: description.trim(),
      expected: expected.trim() === '' ? null : expected.trim(),
      contextLabel: 'Corryk terminal',
      diagnosticsIncluded: includeDiagnostics,
      diagnostics: includeDiagnostics ? buildDiagnosticContext() : null,
      userEmail: userEmail ?? null,
      userName: userDisplayName,
    });
    setSubmittedId(res.data.id);
    resetForm();
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay style={overlayStyle} />
        <Dialog.Content aria-describedby={undefined} style={contentStyle}>
          <header style={headerStyle}>
            <span />
            <Dialog.Title style={titleStyle}>Feedback</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close feedback form" style={iconButtonStyle}>
                <X size={14} />
              </button>
            </Dialog.Close>
          </header>

          <form onSubmit={onSubmit} style={formStyle}>
            <label style={labelStyle} htmlFor="feedback-category">
              Category
            </label>
            <select
              id="feedback-category"
              value={category}
              onChange={(event) => setCategory(event.target.value as Category)}
              style={selectStyle}
            >
              {CATEGORIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What did you expect, and what happened instead?"
              rows={5}
              style={textareaStyle}
            />

            <textarea
              value={expected}
              onChange={(event) => setExpected(event.target.value)}
              placeholder="Steps to reproduce, affected company/market, or what you were trying to do..."
              rows={3}
              style={smallTextareaStyle}
            />

            <div style={inlineRowStyle}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={includeDiagnostics}
                  onChange={(event) => setIncludeDiagnostics(event.target.checked)}
                  style={checkboxStyle}
                />
                <span>Attach terminal diagnostics</span>
              </label>
              <span style={diagnosticHintStyle}>
                Workspace state, recent app/API errors, browser and timestamp.
              </span>
            </div>

            <div style={severityRowStyle}>
              <span style={{ color: 'var(--text-tertiary)' }}>Severity</span>
              {SEVERITIES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSeverity(option.value)}
                  aria-pressed={severity === option.value}
                  style={severityButtonStyle(severity === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div style={diagnosticBoxStyle}>
              <div>
                {includeDiagnostics
                  ? 'Terminal diagnostics will be attached.'
                  : 'Terminal diagnostics are off.'}
              </div>
              <div style={{ color: 'var(--text-tertiary)' }}>
                {diagnosticCount} recent issue{diagnosticCount === 1 ? '' : 's'} captured
              </div>
            </div>

            {submitFeedback.isError && (
              <div role="alert" style={errorStyle}>
                Feedback could not be submitted. Please try again.
              </div>
            )}
            {submittedId && (
              <div role="status" style={successStyle}>
                Feedback received. Reference {submittedId.slice(0, 8)}.
              </div>
            )}

            <footer style={footerStyle}>
              <Dialog.Close asChild>
                <button type="button" style={secondaryButtonStyle}>
                  Cancel
                </button>
              </Dialog.Close>
              <button type="submit" disabled={!canSubmit} style={primaryButtonStyle(!canSubmit)}>
                {submitFeedback.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </footer>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function areaForCategory(category: Category): Area {
  switch (category) {
    case 'data_issue':
      return 'data';
    case 'performance':
      return 'performance';
    case 'feature_request':
      return 'feature';
    case 'bug':
      return 'bug';
    case 'ux_confusion':
      return 'workflow';
  }
}

function buildDiagnosticContext(): Record<string, unknown> {
  const state = useAppStore.getState();
  const activeTab = state.activeTabId ? state.tabs[state.activeTabId] : undefined;
  const panels = activeTab
    ? Object.values(activeTab.panels)
        .slice(0, 16)
        .map((panel) => ({
          id: panel.id,
          component: panel.component,
          title: panel.title,
          link: panel.link,
          localEntityId: panel.localEntityId ?? null,
        }))
    : [];

  return {
    capturedAt: new Date().toISOString(),
    url: typeof window === 'undefined' ? null : window.location.href,
    viewport:
      typeof window === 'undefined'
        ? null
        : {
            width: window.innerWidth,
            height: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,
          },
    browser:
      typeof navigator === 'undefined'
        ? null
        : {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
          },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    appVersion: import.meta.env?.['VITE_APP_VERSION'] ?? null,
    workspace: {
      activeTabId: state.activeTabId,
      activeTabTitle: activeTab?.title ?? null,
      activeCountry: state.filters.activeCountry,
      timeRange: state.filters.timeRange,
      globalCurrency: state.globalCurrency,
      watchlistCount: state.watchlist.length,
      groups: activeTab ? Object.values(activeTab.groups) : [],
      panels,
      totalPanels: activeTab ? Object.keys(activeTab.panels).length : 0,
    },
    recentDiagnostics: getRecentDiagnostics(),
  };
}

// Modal chrome — matches the shared FilterModalShell recipe exactly (overlay
// darkness, width, radius, centered title, lucide X) so every centered modal
// reads as one family.
const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  zIndex: 9499,
};

const contentStyle: CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(560px, 92vw)',
  maxHeight: '85vh',
  background: 'var(--surface)',
  border: '1px solid var(--hairline)',
  borderRadius: 4,
  zIndex: 9500,
  overflow: 'auto',
};

const headerStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '36px 1fr 36px',
  alignItems: 'center',
  padding: 'var(--s-3)',
  borderBottom: '1px solid var(--hairline)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--t-section)',
  fontWeight: 600,
  textAlign: 'center',
};

const iconButtonStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 2,
  color: 'var(--text-secondary)',
  justifySelf: 'end',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 'var(--s-3)',
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--t-meta)',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const selectStyle: CSSProperties = {
  height: 28,
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
  padding: '0 var(--s-2)',
  fontSize: 'var(--t-body)',
};

const textareaBase: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
  padding: 'var(--s-2)',
  fontSize: 'var(--t-body)',
  resize: 'vertical',
  lineHeight: 1.45,
};

const textareaStyle: CSSProperties = {
  ...textareaBase,
  minHeight: 96,
};

const smallTextareaStyle: CSSProperties = {
  ...textareaBase,
  minHeight: 72,
};

const inlineRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const checkboxLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 'var(--t-body)',
  color: 'var(--text-primary)',
};

const checkboxStyle: CSSProperties = {
  width: 14,
  height: 14,
  margin: 0,
  accentColor: 'var(--accent)',
};

const diagnosticHintStyle: CSSProperties = {
  color: 'var(--text-tertiary)',
  fontSize: 'var(--t-meta)',
};

const severityRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 'var(--t-meta)',
};

function severityButtonStyle(active: boolean): CSSProperties {
  return {
    height: 26,
    padding: '0 var(--s-2)',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--hairline)'}`,
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--accent)' : 'var(--menu-surface)',
    color: active ? 'var(--bg)' : 'var(--text-secondary)',
    fontSize: 'var(--t-meta)',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
  };
}

const diagnosticBoxStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  padding: 'var(--s-2)',
  fontSize: 'var(--t-meta)',
  color: 'var(--text-secondary)',
  background: 'var(--surface-elevated)',
};

const errorStyle: CSSProperties = {
  border: '1px solid var(--alert, #c0143c)',
  borderRadius: 2,
  padding: '8px 10px',
  color: 'var(--alert, #c0143c)',
  fontSize: 'var(--t-meta)',
};

const successStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  color: 'var(--positive, #0a7d5a)',
  fontSize: 'var(--t-meta)',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 4,
};

const secondaryButtonStyle: CSSProperties = {
  height: 32,
  padding: '0 13px',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
  fontSize: 'var(--t-body)',
  fontWeight: 600,
  cursor: 'pointer',
};

// Accent-filled primary — the platform's one primary-button treatment (the
// tokens.css [data-accent] recipe), not a bespoke near-black fill.
function primaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    height: 32,
    padding: '0 14px',
    border: `1px solid ${disabled ? 'var(--hairline)' : 'var(--accent)'}`,
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--surface-elevated)' : 'var(--accent)',
    color: disabled ? 'var(--text-tertiary)' : 'var(--bg)',
    fontSize: 'var(--t-body)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
